const { createClientConfig, libConfig } = require('@sstdev/lib_config');
const get = require('lodash.get');

const loggingConfig = createClientConfig({
    definitions: libConfig.logging
});
/**
 * Supported Log Levels
 * @readonly
 * @enum {number}
 */
const logLevels = Object.freeze({
    ERROR: 0,
    WARN: 1,
    INFO: 2,
    DEBUG: 3
});

//https://cloud.google.com/logging/docs/reference/v2/rest/v2/LogEntry#logseverity
const asGoogleLogLevel = (logLevel = logLevels.INFO) => {
    switch (logLevel) {
        case logLevels.ERROR:
            return 500; //ERROR
        case logLevels.WARN:
            return 400; //WARNING
        case logLevels.INFO:
            return 200; //INFO
        case logLevels.DEBUG:
            return 100; //DEBUG
    }
};

function interpretParams(params) {
    if (!params) return { message: [] };
    if (!Array.isArray(params)) return { message: [params] };
    params = params.filter(x => typeof x !== 'undefined' && x !== null); //exclude empty values
    if (!params.length) return { message: [] };
    if (params[params.length - 1].tenantId ?? params[params.length - 1].correlation) {
        const userContext = params.pop();
        return { userContext, message: params };
    }
    return { message: params };
}

function stringify(...parameters) {
    return parameters
        .reduce((resultSofar, val) => {
            let output = val;
            if (val instanceof Error) {
                if (val.stack) {
                    output = val.stack;
                } else {
                    output = val.message;
                }
            } else if (Array.isArray(val)) {
                output = stringify(...val);
            } else if (typeof val === 'object') {
                output = JSON.stringify(val);
            } else if (typeof val !== 'string') {
                output == val.toString();
            }
            resultSofar.push(output);
            return resultSofar;
        }, [])
        .join(' ');
}

function rewindStack(error, levels) {
    let stack = error.stack;
    let [message, ...lines] = stack.split(/[\r\n]+/g);
    error.stack = [message, ...lines.splice(levels)].join('\n');
    return error;
}

/**
 *
 * @param {Array<string|object>} message
 * @param {*} userContext
 * @param {logLevels} logLevel
 * @returns
 */
function formatMessage(message, userContext = {}, logLevel) {
    const logContextInfo = loggingConfig.getConfig(libConfig.logging.logContextInfo, {
        bypassCache: true
    });
    // console.log, etc, can be called with a variable length of arguments
    // Similarly lib_logging.log, etc, can take multiple strings, or even objects as arguments
    // The LAST of those is typically the userContext, which is stripped off earlier and passed in explicitly.
    // However, the FIRST of those is going to be the "main" message. The rest if "additional" information/data
    let [mainMessage, details] = split(message);
    let response;
    // When we are running as a GKE pod, we can log a structured JSON object to stdOut,
    // and get some additional features, specifically:
    // .message is displayed as the summary line
    // .severity is used as log level
    // additional properties _at the root level_ can be used to report on
    if (logContextInfo) {
        let stack;
        if (mainMessage instanceof Error) {
            // Use the error message as the displayed message in the summary,
            // but nest the stack inside of the message
            stack = mainMessage.stack;
            mainMessage = mainMessage.message;
        }
        response = {
            // this is shown in the summary line:
            message: mainMessage,
            //all of this is used for easy filtering/drillign down:
            tenant: {
                title: get(userContext, 'activeTenant.identity:tenant.title'),
                _id: userContext.tenantId
            },
            useCase: {
                title: get(userContext, 'appContext.appName'),
                _id: userContext.appId
            },
            user: {
                title: userContext.userName,
                _id: userContext.userId
            },
            correlation: userContext.correlation,
            groupNumber: userContext.groupNumber
        };
        // If we had an error, nest the stack inside the log entry:
        if (stack) {
            response.stack = stack;
        }
        //for any additional parameters passed into the logging call
        response = details.reduce((sofar, info) => {
            // if it is an object, merge it in at root level,
            // It most likely is things like statistics (duration, etc)
            // If they are in the root, we can run more advanced reporting on them.
            if (typeof info === 'object') {
                return { ...sofar, ...info };
            }
            // otherwise, It's probably a string.
            // stick it in a separate "details" array, so it doesn't mess up the ".message" and make the summary line a pain.
            sofar.details = sofar.details || [];
            sofar.details.push(info);
            return sofar;
        }, response);

        // Tag on a .severity to allow using google log levels (glog simply intercepts all stdOut)
        response.severity = asGoogleLogLevel(logLevel);
    } else {
        // If we are simply writing to a normal console,
        // concat any useful information into a single line:
        if (details.length) {
            response = stringify([mainMessage.stack || mainMessage, ...details]);
        } else {
            response = mainMessage.stack || mainMessage;
        }
        //and decorate it with additional correlation information:
        if (userContext.correlation) {
            if (
                (userContext.correlation.id && !response.includes(userContext.correlation.id)) ||
                (userContext.correlation.mqId && !response.includes(userContext.correlation.mqId))
            ) {
                response = `${userContext.correlation.id || ''} - ${response} - ${userContext.correlation.mqId || ''}`;
            }
        }
    }
    return response;
}

function split(array = []) {
    if (!Array.isArray(array)) {
        return [array, []];
    }
    const remainder = [...array];
    const [head] = remainder.splice(0, 1);
    return [head, remainder];
}

module.exports = {
    logLevels,
    /**
     * @param {Array} optionalParamsLogLevelAndUserContext array of parameters
     * @returns {ParsedParams}
     */
    // parseParams,
    interpretParams,
    stringify,
    rewindStack,
    formatMessage
};
